import type {
  MarketingFlashSaleBlockConfig,
  MarketingFlashSaleCustomerSegment,
  MarketingFlashSaleProductListResponse,
  MarketingFlashSaleStyleConfig,
  MarketingFlashSaleVisibleState,
} from '../../types';

function normalizeText(value?: string) {
  return typeof value === 'string' ? value.trim() : '';
}

function getCustomerStyle(
  block: MarketingFlashSaleBlockConfig,
  customerSegment?: MarketingFlashSaleCustomerSegment,
) {
  return customerSegment === 'old_customer'
    ? block.old_customer_style || block.new_customer_style
    : block.new_customer_style || block.old_customer_style;
}

function getDisplayStyle(
  block: MarketingFlashSaleBlockConfig,
  visibleState: MarketingFlashSaleVisibleState,
  customerSegment?: MarketingFlashSaleCustomerSegment,
) {
  if (visibleState === 'after_limit') {
    return block.reached_limit_style || getCustomerStyle(block, customerSegment);
  }

  if (visibleState === 'old_customer') {
    return block.old_customer_style || block.new_customer_style;
  }

  return block.new_customer_style || block.old_customer_style;
}

function getEffectiveVisibleState(
  response: MarketingFlashSaleProductListResponse,
  customerSegment: MarketingFlashSaleCustomerSegment,
): MarketingFlashSaleVisibleState {
  return response.visible_state || customerSegment;
}

function applyDisplayStyle(
  block: MarketingFlashSaleBlockConfig,
  style?: MarketingFlashSaleStyleConfig,
): MarketingFlashSaleBlockConfig {
  if (!style) {
    return block;
  }

  return {
    ...block,
    title: normalizeText(style.title) || block.title,
    sub_title: normalizeText(style.sub_title) || block.sub_title,
    button_text: normalizeText(style.button_text) || block.button_text,
    background_color: normalizeText(style.background_color) || block.background_color,
    bg_pc_image: normalizeText(style.bg_pc_image) || block.bg_pc_image,
    bg_wap_image: normalizeText(style.bg_wap_image) || block.bg_wap_image,
    button_pc_image: normalizeText(style.button_pc_image) || block.button_pc_image,
    button_wap_image: normalizeText(style.button_wap_image) || block.button_wap_image,
    button_color: normalizeText(style.button_color) || block.button_color,
  };
}

export function resolveMarketingFlashSaleBlock(
  block: MarketingFlashSaleBlockConfig,
  response: MarketingFlashSaleProductListResponse,
): MarketingFlashSaleBlockConfig {
  const customerSegment =
    response.customer_segment === 'old_customer' ? 'old_customer' : 'new_customer';
  const visibleState = getEffectiveVisibleState(response, customerSegment);
  const isHidden = visibleState === 'hidden';
  const isAfterLimit = visibleState === 'after_limit';
  const style = getDisplayStyle(block, visibleState, customerSegment);
  const styledBlock = applyDisplayStyle(block, style);
  const products = Array.isArray(response.items)
    ? response.items.filter(
        (item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object',
      )
    : [];

  return {
    ...styledBlock,
    visible: block.visible && !isHidden,
    status: response.status,
    visible_state: visibleState,
    flash_sale_id: response.flash_sale_id || response.ref_id || styledBlock.flash_sale_id,
    customer_segment: customerSegment,
    reach_limit_rule: response.reach_limit_rule || block.reach_limit_rule,
    participated: response.participated,
    last_paid_date: response.last_paid_date,
    last_paid_participation_date: response.last_paid_participation_date,
    is_reached_limit: response.is_reached_limit,
    is_targeted: response.is_targeted,
    products: isHidden ? [] : products,
    countdown_end_at: isAfterLimit ? null : response.countdown_end_at,
    remaining_seconds: isAfterLimit ? 0 : response.remaining_seconds,
  };
}

export function shouldRefreshMarketingFlashSaleForToken(
  previousToken: string | undefined,
  currentToken: string,
) {
  return previousToken === undefined || previousToken !== currentToken;
}

export function isMarketingFlashSaleAfterLimit(block: MarketingFlashSaleBlockConfig) {
  return block.visible_state === 'after_limit';
}

export function shouldShowMarketingFlashSaleCountdown(block: MarketingFlashSaleBlockConfig) {
  return !isMarketingFlashSaleAfterLimit(block) && block.remaining_seconds !== 0;
}

export function isMarketingFlashSaleProductSoldOut(product: Record<string, unknown>) {
  const status =
    product.status === null || product.status === '' || product.status === undefined
      ? undefined
      : Number(product.status);
  const stock =
    product.stock === null || product.stock === '' || product.stock === undefined
      ? undefined
      : Number(product.stock);

  return (
    (Number.isFinite(status) && status !== 1) ||
    (Number.isFinite(stock) && stock <= 0) ||
    String(product.in_stock) === '2'
  );
}

export function selectMarketingFlashSaleProducts(
  products: Array<Record<string, unknown>> | undefined,
  displayCount: number,
  soldOutDisplay?: number | string | null,
  soldOutSort?: number | string | null,
) {
  if (!Array.isArray(products) || displayCount <= 0) {
    return [];
  }

  const shouldHideSoldOut = Number(soldOutDisplay) === 2;
  const shouldSortSoldOutLast = Number(soldOutSort) === 2;
  const visibleProducts = shouldHideSoldOut
    ? products.filter((product) => !isMarketingFlashSaleProductSoldOut(product))
    : products;
  const orderedProducts = shouldSortSoldOutLast
    ? [...visibleProducts].sort(
        (a, b) =>
          Number(isMarketingFlashSaleProductSoldOut(a)) -
          Number(isMarketingFlashSaleProductSoldOut(b)),
      )
    : visibleProducts;

  return orderedProducts.slice(0, displayCount);
}
